import { I3pService } from '@nuxtDatakodeUtils/services/i3p.service'
import type { I3pServiceInterface } from '@nuxtDatakodeUtils/services/i3p.service.interface'
import { TransformerService } from '@nuxtDatakodeUtils/services/transformer.service'
import type { Filter } from '@nuxtDatakodeUtils/types/Api/Response/Filter'
import { Entity } from '~/models/Entity'
import type { ApplicationTable } from '~/models/ApplicationTable'
import type { Application } from '~/models/Application'

type GenericFieldData = PildomModules.Generic.Data.GenericFieldData
type GenericTableData = PildomModules.Generic.Data.GenericTableData
type GenericApplicationData = PildomModules.Generic.Data.GenericApplicationData

export class TableI3pService extends I3pService<any> implements I3pServiceInterface {
    public table: ApplicationTable
    public application: Application
    public tableData: GenericTableData
    public applicationData: GenericApplicationData

    constructor(table: ApplicationTable | null = null) {
        super()
        if (table) {
            this.table = table
            this.application = table.application
            this.tableData = this.table.data as GenericTableData
            this.applicationData = this.application.data as GenericApplicationData
            this.loadData().then()
        }
    }

    getModel(): Entity {
        return new Entity(this.table)
    }

    getGeometrySubtype(): string {
        return this.tableData?.geometry_subtype
    }

    isGeometryMultiple(): boolean {
        return this.tableData?.is_multiple
    }

    getLayerName(): string {
        return this.tableData.title
    }

    getColumns(): object[] {
        const columnData = this.tableData.list_columns.map((column: GenericFieldData) => {
            return {
                dataKey: column.name,
                translation: column.title,
                ...this.getFormatColumn(column.field_type, column),
            }
        })
        this.addCredentialsColumns(columnData)
        return columnData.length > 0 ? columnData : [{ dataKey: 'uuid', translation: 'Identifiant' }] // si aucune champs on affiche le champs id
    }

    addCredentialsColumns(list: object[]) {
        const transformer = new TransformerService()

        const addColumn = (columnName: string) => {
            list.push({
                dataKey: columnName,
                transform: [transformer.isDateTime.bind(transformer)],
            })
        }

        if (this.tableData?.display_creation_date) {
            addColumn('credentials.created_at')
        }

        if (this.tableData?.display_modification_date) {
            addColumn('credentials.updated_at')
        }
    }

    getFormatColumn(format: string, column: GenericFieldData) {
        const transformer = new TransformerService()
        let formatted = {}

        if (format === 'Number') {
            formatted = {
                transform: [transformer.isNumber.bind(transformer)],
            }
        }

        if (format === 'Date') {
            const isDateRange = column.rules.includes('array')
            formatted = isDateRange
                ? {
                        transform: [transformer.isDateRange.bind(transformer)],
                    }
                : {
                        transform: [transformer.isDate.bind(transformer)],
                    }
        }
        if (format === 'TextArea') {
            formatted = {
                template: [transformer.isCroppedWithTooltip.bind(transformer)],
                transform: [transformer.isCroppedField.bind(transformer)],
            }
        }
        if (format === 'Url') {
            formatted = {
                transform: [transformer.isUrl.bind(transformer)],
            }
        }
        if (format === 'CheckBox') {
            const isTag = column.is_tag
            const options = column.options
            formatted = isTag
                ? {
                        template: [transformer.isBooleanChipsWithColors.bind(transformer, options)],
                    }
                : {
                        transform: [transformer.isBooleanText.bind(transformer)],
                    }
        }

        if (['Radio', 'Select'].includes(format)) {
            const options = column.options
            const isTag = column.is_tag
            formatted = {
                template: [transformer.isMagicChips.bind(transformer, options, isTag)],
            }
        }

        return formatted
    }

    getFilters(): Filter[] {
        const allFilters = this.getAvailableFilters()
        if (allFilters?.length) {
            return allFilters.map((filter: GenericFieldData) => {
                const matchedFilter = this.tableData?.list_filters.find(f => f.name === filter.name)
                // debugger
                return {
                    ...filter,
                    translation: matchedFilter?.title,
                    options: matchedFilter?.options,
                    field_type: matchedFilter?.field_type,
                    is_tag: matchedFilter?.is_tag,
                }
            })
        }
        return []
    }

    getBaseUrl(): string {
        return this.table.getEndPoint()
    }

    getFrontBaseUrl(): string {
        return `/${this.applicationData.slug}/${this.tableData.slug}`
    }

    getExportBaseUrl(): string {
        return this.getModel().getEndPoint()
    }

    getImportsBaseUrl(): string {
        return this.getModel().getEndPoint().replace(/\/entities$/, '/imports')
    }

    getImportsPermissionName(): string {
        return this.getPermissionName().replace(/_magicModel$/, '_genericEntitiesImport')
    }

    getPermissionName(): string {
        return this.getModel().getPermissionName()
    }

    getImportsTranslationName(): string {
        return `generic.imports`
    }
}
